import React from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Icon, Text, type IconName } from '@components'

export interface NotificationAction {
  key: string
  label: string
  icon?: IconName
  onPress: () => void
}

/**
 * The actions of a notification card, kept light: only the first one is a
 * button (white, with a blue outline like the badge on the photo), the
 * others are plain blue text.
 */
export const NotificationActions = ({
  actions,
}: {
  actions: NotificationAction[]
}): React.ReactElement | null => {
  const theme = useTheme()
  const styles = getStyle(theme)
  if (actions.length === 0) return null

  return (
    <View style={styles.row}>
      {actions.map((action, index) => {
        const isMain = index === 0
        return (
          <TouchableOpacity
            key={action.key}
            testID={`notification-action-${action.key}`}
            accessibilityRole="button"
            activeOpacity={0.7}
            onPress={action.onPress}
            style={isMain ? styles.mainButton : styles.textButton}
          >
            {action.icon ? (
              <Icon name={action.icon} size={theme.iconSize.small} color={theme.colors.primary} />
            ) : null}
            <Text type={isMain ? 'bodySmallBold' : 'bodySmall'} color={theme.colors.primary}>
              {action.label}
            </Text>
          </TouchableOpacity>
        )
      })}
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing.xs,
      marginTop: theme.spacing.xxs,
    },
    mainButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      height: 34,
      paddingHorizontal: theme.spacing.lg,
      borderRadius: 17,
      borderWidth: 2,
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.white,
      marginRight: theme.spacing.xs,
    },
    textButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      height: 34,
      paddingHorizontal: theme.spacing.sm,
    },
  })
